import moment from "moment";

import { getEventColor } from "./colors-utils";
import { arrayPropertyExistsNotEmpty, isNotEmptyObject } from "./utils";

const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};
const convertDateToSwedishFormat = (dateString = "") => {
    let value = "";
    if (typeof dateString === "string") {
        if (dateString && dateString.length > 0) {
            const date = new Date(dateString);
            const weekDaysFromEnglishToSwedish = capitalizeFirstLetter(
                date.toLocaleString("sv-SE", { weekday: "long" })
            );
            const monthsFromEnglishToSwedish = date.toLocaleString("sv-SE", { month: "long" });
            const year = date.getFullYear();
            const day = date.toLocaleString("default", { day: "numeric" });

            value = `${weekDaysFromEnglishToSwedish} ${day} ${monthsFromEnglishToSwedish} ${year}`;
        }
    }
    return value;
};

const listOfOperatorer = (ass_operatorer = [], huvudoperator = "") => {
    let list = [];

    if (huvudoperator.length > 0) list.push(`Operatör ${huvudoperator}`);

    if (arrayPropertyExistsNotEmpty(ass_operatorer)) {
        ass_operatorer.forEach((operator, index) => {
            if (index === 0) {
                list.push(`Assisterande operatör ${operator.user_display?.name}`);
            } else {
                list.push(operator.user_display?.name);
            }
        });
    }

    return list.join(", ");
};

const huvudoperationskodText = (Obj = {}) => {
    let text = "";
    if (Obj && isNotEmptyObject(Obj)) {
        text = `${Obj.code} ${Obj.displayName}`;
    }
    return text;
};

const formatInstallelsetid = (installelsedatum, installelsetid) => {
    let installelseString = installelsetid ? `kl ${installelsetid.slice(0, 5)} ` : "";
    installelseString += installelsedatum ?? "";
    return installelseString;
};

const createEventStyle = (id) => {
    const { backgroundColor, textColor } = getEventColor(id);

    return [backgroundColor, textColor];
};

const createSparradTidEvent = (sparradTid) => {
    const event = {
        start: sparradTid.starttid_tidpunkt,
        end: sparradTid.sluttid_tidpunkt,
        resourceId: sparradTid.sal,
        classNames: ["sparrad-tid-event"],
        extendedProps: {
            id: sparradTid.id,
            sparradTid: true,
            datum: sparradTid.datum,
            starttid: sparradTid.starttid,
            sluttid: sparradTid.sluttid,
            sal: sparradTid.sal_display,
            orsak: sparradTid.orsak_display ?? "",
            duration: sparradTid.duration,
            kommentar: sparradTid.kommentar,
        },
    };

    return event;
};

const createEvent = (anmalan, colorId, show = true) => {
    const [backgroundColor, textColor] = createEventStyle(colorId);

    const event = {
        id: anmalan.id,
        allDay: anmalan.bokad_operation_tid ? false : true,
        date: anmalan.bokad_operation_datum,
        classNames: [],
        start: anmalan.salstid_tidpunkt_start
            ? new Date(anmalan.salstid_tidpunkt_start)
            : new Date(anmalan.bokad_operation_datum),
        end: anmalan.salstid_tidpunkt_slut ? new Date(anmalan.salstid_tidpunkt_slut) : null,
        resourceId: anmalan.bokad_sal ?? "0",
        backgroundColor,
        textColor,
        extendedProps: {
            id: anmalan.id,
            salstid: anmalan.salstid,
            forberedelser: anmalan.forberedelser ?? 0,
            optid: anmalan.optid ?? 0,
            avveckling: anmalan.avveckling ?? 0,
            personId: anmalan.patient_display?.personId ?? "",
            personId_formatted: anmalan.patient_display?.personId_formatted ?? "",
            patientId: anmalan.patient ?? null,
            opkod: anmalan.huvudoperationskod_display?.code ?? "",
            showPopup: false,
            personNamn: anmalan.patient_display?.namn ?? "",
            sal: anmalan.bokad_sal_display ?? null,
            huvudoperator: anmalan.current_bokad_huvudoperator_display ?? {},
            huvudoperatorEjBestamd: anmalan.current_bokad_huvudoperator_ej_bestamd,
            lateralitet: anmalan.lateralitet,
            lateralitetDisplay: anmalan.lateralitet_display,
            currentAnestesi: anmalan.current_anestesi,
            anestesiDisplay: getAnestesiAndASA(anmalan),
            operatorer: listOfOperatorer(
                anmalan.ass_operatorer_display,
                anmalan.current_bokad_huvudoperator_ej_bestamd
                    ? "ej bestämd"
                    : anmalan.current_bokad_huvudoperator_display?.user_display?.name
            ),
            huvudoperation: huvudoperationskodText(anmalan.huvudoperationskod_display),
            startDateInSwedish: convertDateToSwedishFormat(anmalan.bokad_operation_datum ?? ""),
            resurskrockar: getResurskrockar(anmalan),
            installelsedatum: anmalan.installelsedatum,
            installelsetid: anmalan.installelsetid,
            avtal: anmalan.avtal_display?.namn ?? "",
            optidTidpunktStart: anmalan.optid_tidpunkt_start ?? "",
            aktuellPreanestesibedomning: anmalan.aktuell_preanestesibedomning?.id,
            status: anmalan.status,
            preliminarbokning: anmalan.preliminarbokning,
            nivaer: getNivaer(anmalan),
            implantat: getImplantat(anmalan),
            operationsinformation: anmalan.operationsinformation,
        },
    };

    if (!anmalan.bokad_operation_tid) {
        event.classNames.push("all-day-event");
    }
    if (!show) {
        event.classNames.push("hidden-event");
    }

    return event;
};

const getImplantat = (anmalan) => anmalan.implantat_display.map((implantat) => implantat.namn).join(", ");

const getAnestesiAndASA = (anmalan) => {
    const displayName = anmalan.current_anestesi?.displayName ?? "";
    if (anmalan.aktuell_preanestesibedomning?.asa) {
        return displayName + ", " + anmalan.aktuell_preanestesibedomning.asa;
    }
    return displayName;
};

const getResurskrockar = (anmalan) =>
    anmalan.resurskrockar?.map((resursId) => anmalan.resurser_display?.find((resurs) => resurs.id === resursId)) ?? [];

/**
 * Hämtar nivåer i formatet "från1-till1, från2-till2"
 * @param anmalan Operationsanmälan-objekt
 * @returns {string}
 */
const getNivaer = (anmalan) => {
    if (!Array.isArray(anmalan.niva_fran_till_display)) {
        return "";
    }
    return anmalan.niva_fran_till_display
        .map((niva) => {
            const fran = niva.fran ?? "";
            const till = niva.till ?? "";
            return `${fran}-${till}`;
        })
        .join(", ");
};

const setEventInfo = (event, anmalan, colorId, onlyBookingInfo, show = true) => {
    setEventColors(event, colorId);

    // Gå igenom - behövs allt det här sättas eller sätts vissa props automatiskt?
    if (anmalan.salstid_tidpunkt_start) {
        event.setDates(new Date(anmalan.salstid_tidpunkt_start), new Date(anmalan.salstid_tidpunkt_slut), {
            allDay: false,
        });
        event.setProp(
            "classNames",
            event.classNames.filter((name) => name !== "all-day-event")
        );
    } else {
        event.setDates(anmalan.bokad_operation_datum, null, { allDay: true });
        event.setProp("classNames", [...event.classNames, "all-day-event"]);
    }

    if (anmalan.bokad_sal_display) {
        event.setExtendedProp("sal", anmalan.bokad_sal_display);
        event.setResources([anmalan.bokad_sal]);
    } else {
        event.setExtendedProp("sal", null);
        event.setResources(["0"]);
    }

    event.setExtendedProp("huvudoperator", anmalan.current_bokad_huvudoperator_display);
    event.setExtendedProp("huvudoperatorEjBestamd", anmalan.current_bokad_huvudoperator_ej_bestamd);

    event.setExtendedProp("installelsedatum", anmalan.installelsedatum);
    event.setExtendedProp("installelsetid", anmalan.installelsetid);

    event.setExtendedProp("resurskrockar", getResurskrockar(anmalan));
    event.setExtendedProp("startDateInSwedish", convertDateToSwedishFormat(anmalan.bokad_operation_datum ?? ""));
    event.setExtendedProp("status", anmalan.status);
    event.setExtendedProp("preliminarbokning", anmalan.preliminarbokning);
    event.setExtendedProp("nivaer", getNivaer(anmalan));

    if (!onlyBookingInfo) {
        event.setExtendedProp("id", anmalan.id);
        event.setExtendedProp("salstid", anmalan.salstid);
        event.setExtendedProp("forberedelser", anmalan.forberedelser);
        event.setExtendedProp("optid", anmalan.optid);
        event.setExtendedProp("avveckling", anmalan.avveckling);
        event.setExtendedProp("personId", anmalan.patient_display.personId);
        event.setExtendedProp("personId_formatted", anmalan.patient_display.personId_formatted);
        event.setExtendedProp("patientId", anmalan.patient);
        event.setExtendedProp("opkod", anmalan.huvudoperationskod_display.code);
        event.setExtendedProp("huvudoperation", huvudoperationskodText(anmalan.huvudoperationskod_display));
        event.setExtendedProp("showPopup", false);
        event.setExtendedProp("personNamn", anmalan.patient_display.namn_reverse);
        event.setExtendedProp(
            "operatorer",
            listOfOperatorer(
                anmalan.ass_operatorer_display,
                anmalan.current_bokad_huvudoperator_ej_bestamd
                    ? "Operatör ej bestämd"
                    : anmalan.current_bokad_huvudoperator_display?.user_display?.name
            )
        );
        event.setExtendedProp("avtal", anmalan.avtal_display?.namn ?? "");
        event.setExtendedProp("currentAnestesi", anmalan.current_anestesi);
        event.setExtendedProp("anestesiDisplay", getAnestesiAndASA(anmalan));
        event.setExtendedProp("status", anmalan.status);
        event.setExtendedProp("implantat", getImplantat(anmalan));
        event.setExtendedProp("operationsinformation", anmalan.operationsinformation);
    }
};

const setEventColors = (event, salId) => {
    const [backgroundColor, textColor] = createEventStyle(salId);
    event.setProp("backgroundColor", backgroundColor);
    event.setProp("textColor", textColor);
};

const diffMinutes = (start, end) => {
    if (!start || !end) {
        return null;
    }
    return Math.ceil(moment(end).diff(moment(start), "minutes", true));
};

export {
    capitalizeFirstLetter,
    createEvent,
    createSparradTidEvent,
    convertDateToSwedishFormat,
    getAnestesiAndASA,
    huvudoperationskodText,
    listOfOperatorer,
    setEventInfo,
    setEventColors,
    createEventStyle,
    formatInstallelsetid,
    diffMinutes,
};
